import React, { useState, forwardRef, Ref } from 'react';
import styled from 'styled-components';
import { Link } from '@reach/router';
import Label from './Label';
import { User, AdminStatus } from 'state/user/UserTypes';
import { determineUserTier, convertTimeStamp, truncate, deriveDateFromMongoId, presentErrorBasedOnCode } from 'utils';
import LinkGroup from './LinkGroup';
import { updateUser } from 'state/user/UserActions';
import { Error, ErrorCodes } from 'types';
import ErrorMessage from './ErrorMessage';
import { SubscriptionTiers } from 'state/subscription/SubscriptionTypes';
import useViewport from 'hooks/useViewport';
import SmallLoader from './SmallLoader';
import ToggleButton from 'components/CustomerToggleButton';

interface Props {
   user: User;
   updateCustomerList: (customer: User) => void;
}

const ListItemCustomer = forwardRef(({ user, updateCustomerList }: Props, ref: Ref<HTMLDivElement>) => {
   const [togglingLegacyStatus, setTogglingLegacyStatus] = useState<boolean>(false);
   const [error, setError] = useState<Error | null>(null);
   const [{ width: windowWidth }] = useViewport();

   const toggleStatus = async (): Promise<void> => {
      try {
         await setTogglingLegacyStatus(true);
         const updatedCustomer = await updateUser({ isLegacy: !user.isLegacy }, user._id);
         updateCustomerList(updatedCustomer);
      } catch (error) {
         //if error results from missing token it will not have a json method
         error = error.json ? await error.json() : error;
         console.log(error);
         setError(error);
      }
      setTogglingLegacyStatus(false);
   };

   return (
      <>
         <GridContainer ref={ref}>
            <span>
               <Label>Email</Label>
               {windowWidth > 590 ? user.email : truncate(user.email, 26)}
            </span>

            <span>
               <Label>Name</Label>
               {user.firstName || ''} {user.lastName || ''}
            </span>

            {user.phone && (
               <span>
                  <Label>Phone</Label>
                  {user.phone}
               </span>
            )}
            <span>
               <Label>Tier</Label>
               {determineUserTier(user)}
            </span>
            {determineUserTier(user) === SubscriptionTiers.Paid && (
               <span>
                  <Label>Trial end</Label>
                  {convertTimeStamp(user.subscription[0].expirationDate)}
               </span>
            )}
            <span>
               <Label>Created</Label>
               {deriveDateFromMongoId(user._id)}
            </span>
            <ButtonContainer>
               {user.isLegacy && (!user.adminStatus || user.adminStatus === AdminStatus.none) ? (
                  <StyledLinkGroup>
                     <li>
                        <Link to={`${window.location.pathname}/${user._id}`}>Details</Link>
                     </li>
                     <li>
                        <LegacyStatus>Loyalty User</LegacyStatus>
                     </li>
                  </StyledLinkGroup>
               ) : (
                  <Link to={`${window.location.pathname}/${user._id}`} style={{ fontWeight: 600 }}>
                     Details
                  </Link>
               )}
               <LegacyStatusButton user={user} togglingStatus={togglingLegacyStatus} toggle={toggleStatus} />
            </ButtonContainer>
         </GridContainer>
         {error && <ErrorMessage>{presentErrorBasedOnCode(error.code as ErrorCodes)}</ErrorMessage>}
      </>
   );
});

const LegacyStatusButton: React.FC<{ user: User; togglingStatus: boolean; toggle: () => void }> = ({ user, togglingStatus, toggle }): JSX.Element => (
   <ToggleButton onClick={toggle}>
      {user.isLegacy ? togglingStatus ? <SmallLoader /> : 'Revoke Loyalty Status' : togglingStatus ? <SmallLoader /> : 'Grant Loyalty Status'}
   </ToggleButton>
);

export default ListItemCustomer;

const LegacyStatus = styled.span`
   color: ${({ theme }) => theme.secondaryBlueDark};
   font-weight: 800;
`;

const StyledLinkGroup = styled(LinkGroup)`
   @media only screen and (max-width: 415px) {
      margin-bottom: 30px;
   }
`;

const GridContainer = styled.div`
   display: flex;
   flex-wrap: wrap;
   flex-direction: column;
   justify-content: center;
   align-items: flex-start;
   padding: 10px 15px;
   margin: 0 auto;
   background-color: ${({ theme }) => theme.secondaryWhite};
   border-radius: 5px;
   border: 1px solid ${({ theme }) => theme.primaryGrey};
   position: relative;
   :not(:last-child) {
      margin-bottom: 10px;
   }
   @media only screen and (min-width: 1000px) {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
   }
`;

const ButtonContainer = styled.div`
   position: absolute;
   display: flex;
   flex-direction: column;
   justify-content: flex-start;
   padding-bottom: 5px;
   align-items: flex-end;
   align-content: flex-end;
   bottom: 5px;
   right: 15px;
`;
